import React from "react";
import ReactDOM from "react-dom";
import { Modal, Button, Icon } from "antd";
import { ModalProps, ModalFuncProps } from "antd/lib/modal/Modal";
import "./aVhModal.scss";

/**
 * 弹窗的类型，由于新规范约束，此处要定义children，
 * 原本对children设置了类型：React.ReactChild | React.ReactChildren | React.ReactElement<any>[]
 * 最终还是设置成了any，否则只能输入html元素。
 */
interface ModalType extends ModalProps {
  /** 样式名称 */
  className?: string;
  /** 弹窗高度是否满屏显示，是：设置高度  否：设置最大高度 */
  fullHeight?: boolean;
  /** 弹窗中的内容 */
  children: any;
}

/**
 * 简版弹窗类型
 */
interface ModalSimpleType extends ModalFuncProps {
  wrapClassName?: string;
}

const VhModal = (props: ModalType) => {
  const { className, children, fullHeight } = props;

  return (
    <Modal
      visible={true}
      width={600}
      destroyOnClose={true}
      maskClosable={false}
      keyboard={false}
      centered={true}
      {...props}
      className={`vh-modal-default ${className || ""} ${fullHeight ? "full-h" : ""}`}
      wrapClassName={props.wrapClassName || ""}
    >
      {children}
    </Modal>
  )
}

/**
 * 重写简版弹窗方法
 * 由于新规则的约束，修复了方法和参数之间调用逻辑的错误，
 * 修复了以往的一些问题，
 * 并增加了简版弹窗自定义按钮方法和名称
 * @param config 弹窗参数
 */
function makeModal(config: ModalSimpleType) {
  const div = document.createElement("div");
  document.body.appendChild(div);
  config = {
    ...config,
    centered: false,
    title: <>{config.icon}{config.title}</>
  };

  let currentConfig: any = {
    ...config,
    closable: false,
    className: `vh-modal-simple-default ${config.className || ""}`
  };

  function destroy(...args: any[]) {
    return () => {
      const unmountResult = ReactDOM.unmountComponentAtNode(div);
      if (unmountResult && div.parentNode) {
        div.parentNode.removeChild(div);
      }
      const triggerOk = args.some(param => param && param.triggerOk);
      if (config.onOk && triggerOk) {
        config.onOk(...args);
      }
      const triggerCancel = args.some(param => param && param.triggerCancel);
      if (config.onCancel && triggerCancel) {
        config.onCancel(...args);
      }
    }
  }

  function render(props: any) {
    ReactDOM.render(
      <VhModal {...props}>{props.content}</VhModal>,
      div
    );
  }

  function close(...args: any[]) {
    return () => {
      currentConfig = {
        ...currentConfig,
        visible: false,
        afterClose: destroy(...args),
      };
      render(currentConfig);
    }    
  }

  const footer =
    <div>
      {config.type === "confirm" 
      ? <Button onClick={close({triggerCancel: true})}>
          {config.cancelText || "取消"}
        </Button> 
      : null 
      }
      <Button onClick={close({triggerOk: true})} type="primary">
        {config.okText || "确定"}
      </Button>
    </div>;

  currentConfig = {
    ...currentConfig,
    ...config,
    close,
    footer
  };   
  
  function update(newConfig: ModalSimpleType) {
    currentConfig = {
      ...currentConfig,
      ...newConfig,
    };
    render(currentConfig);
  }  
  
  render(currentConfig);
  return {
    destroy: close,
    update
  };
}

/**
 * 简版确认弹窗
 * @param props 弹窗参数
 */
VhModal.confirm = (props: ModalSimpleType) => {
  const config: ModalSimpleType = {
    type: "confirm",
    icon: <Icon className="warning-color" type="exclamation-circle" theme="filled" />,
    title: "确认",
    ...props
  };
  return makeModal(config);
};

/**
 * 简版信息提示弹窗
 * @param props 弹窗参数
 */
VhModal.info = (props: ModalSimpleType) => {
  const config: ModalSimpleType = {
    type: "info",
    icon: <Icon className="warning-color" type="info-circle" theme="filled" />,
    title: "提示",
    ...props
  };
  return makeModal(config);
};

/**
 * 简版成功弹窗
 * @param props 弹窗参数
 */
VhModal.success = (props: ModalSimpleType) => {
  const config: ModalSimpleType = {
    type: "success",
    icon: <Icon className="success-color" type="check-circle" theme="filled" />,
    title: "成功",
    ...props
  };
  return makeModal(config);
};

/**
 * 简版错误弹窗
 * @param props 弹窗参数
 */
VhModal.error = (props: ModalSimpleType) => {
  const config: ModalSimpleType = {
    type: "error",
    icon: <Icon className="error-color" type="close-circle" theme="filled" />,
    title: "错误",
    ...props
  };
  return makeModal(config);
};

/**
 * 简版警告弹窗
 * @param props 弹窗参数
 */
VhModal.warning = (props: ModalSimpleType) => {
  const config: ModalSimpleType = {
    type: "warning",
    icon: <Icon className="warning-color" type="exclamation-circle" theme="filled" />,
    title: "警告",
    ...props
  };
  return makeModal(config);
};

export default VhModal;
